import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";

import { Modal, Input, Textarea, Switch, Button } from "../ui";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";

const schema = yup.object().shape({
  name: yup
    .string()
    .min(2, "Nome muito curto")
    .max(50, "Nome muito longo")
    .required("Nome é obrigatório"),
  greetingMessage: yup.string(),
  farewellMessage: yup.string(),
});

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
  const [isDefault, setIsDefault] = useState(false);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      greetingMessage: "",
      farewellMessage: "",
    },
  });

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) {
        reset({
          name: "",
          greetingMessage: "",
          farewellMessage: "",
        });
        setIsDefault(false);
        setSelectedQueueIds([]);
        return;
      }

      try {
        const { data } = await api.get(`whatsapp/${whatsAppId}`);
        reset({
          name: data.name || "",
          greetingMessage: data.greetingMessage || "",
          farewellMessage: data.farewellMessage || "",
        });
        setIsDefault(data.isDefault || false);
        const whatsQueueIds = data.queues?.map((queue) => queue.id) || [];
        setSelectedQueueIds(whatsQueueIds);
      } catch (err) {
        toastError(err);
      }
    };

    if (open) {
      fetchSession();
    }
  }, [whatsAppId, open, reset]);

  const handleSaveWhatsApp = async (data) => {
    setLoading(true);
    const whatsappData = {
      ...data,
      isDefault,
      queueIds: selectedQueueIds,
    };

    try {
      if (whatsAppId) {
        await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
      } else {
        await api.post("/whatsapp", whatsappData);
      }
      toast.success(i18n.t("whatsappModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset({
      name: "",
      greetingMessage: "",
      farewellMessage: "",
    });
    setIsDefault(false);
    setSelectedQueueIds([]);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        whatsAppId
          ? i18n.t("whatsappModal.title.edit")
          : i18n.t("whatsappModal.title.add")
      }
      size="lg"
      footer={
        <>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {i18n.t("whatsappModal.buttons.cancel")}
          </Button>
          <Button
            type="submit"
            form="whatsapp-form"
            loading={loading}
            disabled={loading}
          >
            {whatsAppId
              ? i18n.t("whatsappModal.buttons.okEdit")
              : i18n.t("whatsappModal.buttons.okAdd")}
          </Button>
        </>
      }
    >
      <form
        id="whatsapp-form"
        onSubmit={handleSubmit(handleSaveWhatsApp)}
        className="space-y-4"
      >
        {/* Name and Default Switch */}
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <Input
              label={i18n.t("whatsappModal.form.name")}
              {...register("name")}
              error={errors.name?.message}
              autoFocus
            />
          </div>
          <div className="pt-7">
            <Switch
              label={i18n.t("whatsappModal.form.default")}
              checked={isDefault}
              onChange={(checked) => setIsDefault(checked)}
            />
          </div>
        </div>

        {/* Greeting Message */}
        <Textarea
          label={i18n.t("queueModal.form.greetingMessage")}
          {...register("greetingMessage")}
          error={errors.greetingMessage?.message}
          rows={4}
          resize="vertical"
        />

        {/* Farewell Message */}
        <Textarea
          label={i18n.t("whatsappModal.form.farewellMessage")}
          {...register("farewellMessage")}
          error={errors.farewellMessage?.message}
          rows={4}
          resize="vertical"
        />

        {/* Queue Select */}
        <QueueSelect
          selectedQueueIds={selectedQueueIds}
          onChange={(selectedIds) => setSelectedQueueIds(selectedIds)}
        />
      </form>
    </Modal>
  );
};

export default React.memo(WhatsAppModal);
